import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine,
} from 'recharts';
import { RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

// Aba KPIs (Adnéia, 28/10/2026): os números do atendimento que o Allan pediu, vivos. Período
// rápido (semana passada, mês, 3 meses), o valor contra o período anterior de mesmo tamanho, a
// meta só onde está escrita (resolução em 15 dias acima de 40%, estorno abaixo de 10%) e o
// gráfico por dia (semana) ou por semana (mês e 3 meses). Os cancelamentos sem o Tem em Casa
// saem em número e em R$ (produto + frete). Quem vê é o atendimento (admin e atendente). Os
// números vêm prontos da rota /api/dashboard/v2/kpis; a tela só mostra. Sem dado, "?".
const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERIODOS = [
  { chave: 'semana', rotulo: 'Semana passada' },
  { chave: 'mes', rotulo: 'Mês' },
  { chave: '3meses', rotulo: '3 meses' },
];
// As mesmas cores do card "Cancelamentos do período" da aba Volume: a linha tem a mesma cor nas duas.
const CORES_CANC = { aes: '#3b82f6', atendimento: '#ef4444', etr: '#f59e0b', erro_nota: '#8b5cf6' };
const CURTO = { aes: 'AES', atendimento: 'Estornados', etr: 'ETR', erro_nota: 'Erro na Nota' };
const COR_SERIE = '#3b82f6';
const COR_SERIE_2 = '#10b981';
const COR_META = '#dc2626';

const vazio = (v) => v === null || v === undefined;
const num = (v, casas = 0) => (vazio(v) ? '?' : Number(v).toLocaleString('pt-BR', {
  minimumFractionDigits: casas, maximumFractionDigits: casas,
}));
const reais = (v) => (vazio(v) ? 'R$ ?' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
const diaMes = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}` : '?');
const valorKpi = (k, v) => (k.unidade === '%' ? `${num(v, 1)}%` : num(v, k.casas || 0));

// Diferença contra o período anterior. Cinza de propósito: subir não é bom nem ruim em todos.
const Diferenca = ({ k }) => {
  if (vazio(k.valor) || vazio(k.anterior)) {
    return <span className="text-xs text-muted-foreground">vs anterior: ?</span>;
  }
  const d = k.valor - k.anterior;
  const seta = d > 0 ? '▲' : d < 0 ? '▼' : '=';
  const txt = k.unidade === '%' ? `${num(Math.abs(d), 1)} pp` : num(Math.abs(d), k.casas || 0);
  return (
    <span className="text-xs text-muted-foreground">
      {seta} {txt} vs anterior ({valorKpi(k, k.anterior)})
    </span>
  );
};

const SeloMeta = ({ k }) => {
  if (vazio(k.meta)) {
    return <span className="rounded-full border px-2 py-0.5 text-xs text-muted-foreground">• sem meta: medindo</span>;
  }
  if (vazio(k.valor)) {
    return <span className="rounded-full border px-2 py-0.5 text-xs text-muted-foreground">? sem dado · meta {k.meta_txt}</span>;
  }
  const ok = k.sentido === 'acima' ? k.valor > k.meta : k.valor < k.meta;
  return ok ? (
    <span className="rounded-full border border-green-300 px-2 py-0.5 text-xs font-semibold text-green-700 dark:border-green-800 dark:text-green-400">
      ✓ dentro da meta ({k.meta_txt})
    </span>
  ) : (
    <span className="rounded-full border border-red-300 px-2 py-0.5 text-xs font-semibold text-red-700 dark:border-red-800 dark:text-red-400">
      ✕ fora da meta ({k.meta_txt})
    </span>
  );
};

const CartaoKpi = ({ k }) => {
  const duas = k.chave === 'abertos';
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{k.numero ? `Nº ${k.numero} · ${k.resposta}` : k.resposta}</CardDescription>
        <CardTitle className="text-base">{k.rotulo}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-3xl font-bold">{valorKpi(k, k.valor)}</span>
          {duas && <span className="text-sm text-muted-foreground">· {num(k.resolvidos)} resolvidos</span>}
          {k.chave === 'similar' && <span className="text-sm text-muted-foreground">· {reais(k.valor_rs)}</span>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SeloMeta k={k} />
          <Diferenca k={k} />
        </div>
        <div className="h-40">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={k.serie} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-muted" />
              <XAxis dataKey="rotulo" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={k.unidade === '%' || !!k.casas} />
              <Tooltip formatter={(v) => (vazio(v) ? '?' : valorKpi(k, v))} />
              {duas && <Legend wrapperStyle={{ fontSize: 11 }} />}
              <Bar dataKey="valor" name={duas ? 'Abertos' : k.rotulo} fill={COR_SERIE} radius={[3, 3, 0, 0]} />
              {duas && <Bar dataKey="resolvidos" name="Resolvidos" fill={COR_SERIE_2} radius={[3, 3, 0, 0]} />}
              {!vazio(k.meta) && (
                <ReferenceLine y={k.meta} stroke={COR_META} strokeDasharray="5 4"
                  label={{ value: `meta ${k.meta}%`, fill: COR_META, fontSize: 11, position: 'insideTopRight' }} />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
        <p className="text-xs text-muted-foreground">{k.detalhe}</p>
        <p className="text-[11px] text-muted-foreground">Fonte: {k.fonte}.</p>
      </CardContent>
    </Card>
  );
};

const CartaoCancelamentos = ({ c }) => {
  if (!c) return null;
  const ant = c.anterior || {};
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>Parte dos cancelamentos no Nº 5 · em número e em R$</CardDescription>
        <CardTitle className="text-base">Cancelamentos (sem o Tem em Casa)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <span className="text-3xl font-bold">{num(c.total_entregas)}</span>
          <span className="text-xl font-semibold">{reais(c.valor_total)}</span>
          <span className="text-xs text-muted-foreground">
            vs anterior: {num(ant.total_entregas)} · {reais(ant.valor_total)}
          </span>
        </div>
        {c.banco_fora && (
          <p className="text-xs text-amber-700 dark:text-amber-400" role="status">
            O banco da casa não respondeu: AES, ETR, o total e o R$ ficam "?".
          </p>
        )}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Linha</TableHead>
                <TableHead className="text-right">Cancelados</TableHead>
                <TableHead className="text-right">R$</TableHead>
                <TableHead className="text-right">Anterior</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(c.linhas || []).map((l) => {
                const a = (ant.linhas || {})[l.chave] || {};
                return (
                  <TableRow key={l.chave}>
                    <TableCell>
                      <span className="mr-2 inline-block h-2.5 w-2.5 rounded-sm" style={{ background: CORES_CANC[l.chave] }} />
                      {l.rotulo}
                    </TableCell>
                    <TableCell className="text-right">{num(l.cancelados)}</TableCell>
                    <TableCell className="text-right">
                      {reais(l.valor)}
                      {l.sem_valor > 0 && <span className="text-xs text-muted-foreground"> (+{l.sem_valor} sem valor)</span>}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">{num(a.cancelados)} · {reais(a.valor)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div>
            <p className="mb-1 text-xs font-semibold text-muted-foreground">Quantidade</p>
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={c.serie} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-muted" />
                  <XAxis dataKey="rotulo" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip formatter={(v) => num(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {['aes', 'atendimento', 'etr', 'erro_nota'].map((ch) => (
                    <Bar key={ch} dataKey={ch} name={CURTO[ch]} stackId="q" fill={CORES_CANC[ch]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div>
            <p className="mb-1 text-xs font-semibold text-muted-foreground">R$ (produto + frete)</p>
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={c.serie} margin={{ top: 8, right: 8, left: 6, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-muted" />
                  <XAxis dataKey="rotulo" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => num(v)} />
                  <Tooltip formatter={(v) => reais(v)} />
                  <Bar dataKey="valor" name="R$" fill="#64748b" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          A mesma conta do card da aba Volume: AES cancelado de verdade, atendimento estornado, ETR por falta ou perda
          que parou e Erro na Nota sem entrega nova. R$ = produto + frete, a régua da tela Cancelamentos. Na semana,
          o total soma os dias.
        </p>
      </CardContent>
    </Card>
  );
};

const AbaKpis = () => {
  const { getAuthHeader } = useAuth();
  const [periodo, setPeriodo] = useState('semana');
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async (p) => {
    setCarregando(true);
    setErro('');
    try {
      const r = await axios.get(`${API_URL}/api/dashboard/v2/kpis?periodo=${p}`, { headers: getAuthHeader() });
      setDados(r.data);
    } catch (e) {
      setErro(e?.response?.status === 403 ? 'A aba KPIs é do atendimento.' : 'Não deu para ler os KPIs agora.');
    } finally {
      setCarregando(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { carregar(periodo); }, [carregar, periodo]);

  const lido = dados?.lido_em ? `${dados.lido_em.slice(8, 10)}/${dados.lido_em.slice(5, 7)} às ${dados.lido_em.slice(11, 13)}h${dados.lido_em.slice(14, 16)}` : '?';
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">KPIs do atendimento</CardTitle>
          <CardDescription>
            {dados
              ? `De ${diaMes(dados.inicio)} a ${diaMes(dados.fim)} · comparado com ${diaMes(dados.anterior?.inicio)} a ${diaMes(dados.anterior?.fim)} · ${dados.granularidade === 'dia' ? 'por dia' : 'por semana (o rótulo é o primeiro dia)'} · lido em ${lido}`
              : 'Carregando…'}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-2">
          {PERIODOS.map((p) => (
            <Button key={p.chave} size="sm" variant={periodo === p.chave ? 'default' : 'outline'}
              onClick={() => setPeriodo(p.chave)} disabled={carregando}>
              {p.rotulo}
            </Button>
          ))}
          <Button size="sm" variant="ghost" onClick={() => carregar(periodo)} disabled={carregando} aria-label="Atualizar">
            <RefreshCw className={`h-4 w-4 ${carregando ? 'animate-spin' : ''}`} />
          </Button>
          {erro && <span className="text-sm text-red-600" role="alert">{erro}</span>}
        </CardContent>
      </Card>

      {dados && (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {dados.kpis.map((k) => <CartaoKpi key={k.chave} k={k} />)}
            {dados.faltam.map((f) => (
              <Card key={f.numero} className="border-dashed">
                <CardHeader className="pb-2">
                  <CardDescription>Nº {f.numero} · ainda sem dado</CardDescription>
                  <CardTitle className="text-base">{f.rotulo}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex flex-wrap items-baseline gap-3">
                    <span className="text-3xl font-bold">?</span>
                    {f.meta_txt && <span className="rounded-full border px-2 py-0.5 text-xs text-muted-foreground">meta {f.meta_txt}</span>}
                  </div>
                  <p className="text-xs text-muted-foreground">{f.porque}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <CartaoCancelamentos c={dados.cancelamentos} />
          <p className="text-xs text-muted-foreground">
            Os números de foto (estourados, resolvidos em até 15 dias e similar) têm um valor por dia a partir de
            {' '}{dados.fotos_desde ? diaMes(dados.fotos_desde) : 'a primeira foto'}: antes disso, aparecem com "?". A regra de
            cada número está no processo "números oficiais do atendimento" (confirmado em 28/10).
          </p>
        </>
      )}
    </div>
  );
};

export default AbaKpis;
